/**
 * Lean Setup Wizard — Page 3: Team, bays, specialty, parking.
 *
 * Rows = num_technicians from Page 2. Per-row fields:
 *   name, role, skill_tier (1–10), efficiency %, buffer %, shift, bays_allocated.
 *
 * Below the rows:
 *   - Specialty bays (ADAS / Alignment / ADAS+Alignment / Wash). Each
 *     selection converts ONE of the primary tech's existing bays.
 *   - Estimated number of parking spots.
 *
 * 0 bays allowed (FOH / managers / advisors with no bay assignment).
 * Producing techs (Technician / Foreman) with 0 bays get a soft warning.
 */

import { NO_BUFFER_SET_LABEL } from '@/utils/planningBuffer';
import { TechnicianBackgroundFields } from '@/components/technicians/TechnicianBackgroundFields';
import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import {
  page3Schema,
  SPECIALTY_KINDS,
  SPECIALTY_LABELS,
  TEAM_ROLE_OPTIONS,
  TEAM_ROLE_LABELS,
  isInvitableTeamRole,
  reconcileBayTypesForCount,
  softWarningsForPage3,
  type Page3Data,
  type SpecialtyKind,
  type TeamRole,
  type WizardSpecialty,
  type WizardTech,
  type WizardTeamMember,
} from '@/lib/onboarding/wizardValidation';
import { validateTechnicianHoursAgainstShopHours } from '@/utils/technicianHoursAdvisory';
import type { ShopHours } from '@/utils/shopHoursUtils';
import {
  BAY_TYPE_PICKER_OPTIONS,
  DEFAULT_SETUP_BAY_TYPE,
  bayTypeToLabel,
  type BayTypeEnum,
} from '@/utils/bayTypeLabels';

interface TeamBaysSpecialtyParkingStepProps {
  initial: Partial<Page3Data>;
  /** Determines how many TECHNICIAN-team-role rows to render initially (from Page 2). */
  technicianCount: number;
  /** Number of SERVICE_WRITER rows to seed (from Page 2). */
  advisorCount?: number;
  /** Number of MANAGER rows to seed (from Page 2). */
  managerCount?: number;
  /**
   * Shop hours from page 1, used to advise (not block) when a tech's
   * shift falls outside the configured shop bands. Null when page 1
   * hasn't been completed or carries no configured days.
   */
  shopHours?: ShopHours | Record<string, { open?: string; close?: string } | null | undefined> | null;
  onBack: () => void;
  onFinish: (data: Page3Data) => void | Promise<void>;
  isCommitting?: boolean;
  /**
   * Batch 9: emit the current Step 3 draft up to the parent on every
   * change. LeanSetupWizard persists it as page3 state so going back
   * to Step 2 and forward to Step 3 preserves user edits.
   */
  onDraftChange?: (draft: Page3Data) => void;
  /**
   * Batch 9: when the user clicks Add / Remove a row, the parent
   * updates page2 counts so Step 2 stays in sync. `delta` is a
   * partial diff: { tech?: 1, advisor?: -1 } etc.
   */
  onCountDelta?: (delta: { tech?: number; advisor?: number; manager?: number }) => void;
  /**
   * Batch 9C: full inferred technician roster from the import path
   * (`OnboardSetupImportWizard` reads it from the technician_roster
   * inference payload, ordered by job_count desc). When Step 2 tech
   * count is bumped past the initially seeded rows — or the operator
   * clicks Add Team Member — the wizard uses the next unused name
   * from this list before producing a blank row.
   */
  suggestedTechnicianRoster?: readonly string[];
  /**
   * Batch 9C: full distinct advisor roster from
   * shop_history_repair_orders.advisor_display_name, ordered by
   * frequency descending. Same top-up contract as the technician
   * roster.
   */
  suggestedAdvisorRoster?: readonly string[];
}

/**
 * Batch 9F: every row carries a stable client-side ID so Remove,
 * React key, and per-row event handlers stay correctly bound across
 * re-renders even when rows are added/removed mid-session. The
 * pre-9F code used the array index for both the React key and the
 * Remove handler closure — newly-added rows sometimes failed to
 * remove because `setPage3(draft)` from the `onDraftChange` effect
 * caused a parent re-render that interleaved with the click
 * handler's stale closure. Stable IDs eliminate the whole class of
 * index-based bugs.
 *
 * The schema (`wizardTeamMemberSchema`) strips unknown keys on
 * parse, so `_clientId` never reaches commitWizard or the database.
 */
type RowWithClientId = WizardTeamMember & { _clientId: string };

function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `row_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

function blankTech(): RowWithClientId {
  return {
    _clientId: newClientId(),
    team_role: 'TECHNICIAN',
    name: '',
    skill_tier: 5,
    efficiency_percentage: 100,
    // B-W4: a new row has NO buffer set (planned as 0 %) — never a silent 25.
    buffer_percentage: null,
    shift_start: '08:00',
    shift_end: '17:00',
    bays_allocated: 1,
    // Batch 16-B: every new TECHNICIAN row starts with one Flat bay
    // selected. Per-bay dropdowns render below the Bays input; specialty
    // toggles further down still convert the first N bays to their
    // specialty type regardless of the dropdown's choice.
    bay_types: [DEFAULT_SETUP_BAY_TYPE],
  };
}

function blankNonTechRow(team_role: Exclude<TeamRole, 'TECHNICIAN'>): RowWithClientId {
  // Non-technician rows carry only the OptiBay/team role + name. The
  // technician-specific fields are intentionally omitted (the schema
  // makes them optional for non-TECHNICIAN team_role) so they don't
  // round-trip stale defaults if the user later changes the row to
  // TECHNICIAN — they'll be re-defaulted via the dropdown handler.
  return { _clientId: newClientId(), team_role, name: '' };
}

/** Strip the UI-only `_clientId` field before emitting outside the component. */
function stripClientId(row: RowWithClientId | WizardTeamMember): WizardTeamMember {
  if ('_clientId' in row) {
    const { _clientId: _drop, ...rest } = row as RowWithClientId;
    void _drop;
    return rest as WizardTeamMember;
  }
  return row;
}

/** Ensure an inbound `WizardTeamMember` carries a client-side ID. */
function ensureClientId(row: WizardTeamMember | RowWithClientId): RowWithClientId {
  if ('_clientId' in row && typeof row._clientId === 'string') return row as RowWithClientId;
  return { ...row, _clientId: newClientId() };
}

/**
 * Batch 9: reconcile an existing rows array against Step 2 counts.
 *
 *   - Groups existing rows by team_role.
 *   - For each role with a target count: keeps existing rows, tops
 *     up with blanks if too few. When trimming, prefers blank-named
 *     rows first so user-edited rows survive.
 *   - Pass-through rows (OWNER / ORG_ADMIN) are appended verbatim —
 *     Step 2 has no slider for them.
 *
 * Batch 9C: optional `rosters` carry the full inferred technician
 * and advisor name lists. When a role group needs to grow, the
 * helper pulls the NEXT UNUSED name from the appropriate roster
 * before falling back to a blank row. "Unused" = not already
 * present (case-insensitive trim match) in the existing rows.
 * Manual flow callers pass empty arrays; behavior is identical to
 * the Batch 9 reconciler.
 */
function pickUnusedName(
  roster: readonly string[],
  alreadyUsedLowercased: Set<string>,
): string | null {
  for (const candidate of roster) {
    const key = candidate.trim().toLowerCase();
    if (!key) continue;
    if (alreadyUsedLowercased.has(key)) continue;
    alreadyUsedLowercased.add(key);
    return candidate.trim();
  }
  return null;
}

function reconcileRowsAgainstCounts(
  existing: Array<WizardTeamMember | RowWithClientId>,
  counts: { tech: number; advisor: number; manager: number },
  rosters: {
    technicianRoster?: readonly string[];
    advisorRoster?: readonly string[];
  } = {},
): RowWithClientId[] {
  // Batch 9F: stamp a stable _clientId on every row that doesn't
  // already have one (e.g., inbound `initial.technicians` from the
  // import path or a round-trip draft). Inbound rows that already
  // carry an ID keep it so React reconciliation tracks them across
  // re-renders.
  const stamped: RowWithClientId[] = existing.map(ensureClientId);
  const isRole = (r: RowWithClientId, target: TeamRole) =>
    (r.team_role ?? 'TECHNICIAN') === target;
  const techs = stamped.filter((r) => isRole(r, 'TECHNICIAN'));
  const advisors = stamped.filter((r) => isRole(r, 'SERVICE_WRITER'));
  const managers = stamped.filter((r) => isRole(r, 'MANAGER'));
  const passThrough = stamped.filter(
    // WS-ELIG B-E7: Detail Specialist rows have no Step 2 slider either.
    (r) => isRole(r, 'OWNER') || isRole(r, 'ORG_ADMIN') || isRole(r, 'DETAIL_SPECIALIST'),
  );
  // Track names already in use per role so the roster top-up never
  // duplicates a name the operator already has on the row stack.
  const lowerCasedNames = (rows: RowWithClientId[]) =>
    new Set(
      rows
        .map((r) => (r.name ?? '').trim().toLowerCase())
        .filter((n) => n.length > 0),
    );
  const usedTechs = lowerCasedNames(techs);
  const usedAdvisors = lowerCasedNames(advisors);
  const adjust = (
    rows: RowWithClientId[],
    target: number,
    makeBlank: () => RowWithClientId,
    rosterForTopUp: readonly string[] | undefined,
    usedSet: Set<string>,
    setName: (row: RowWithClientId, name: string) => RowWithClientId,
  ): RowWithClientId[] => {
    if (rows.length === target) return rows;
    if (rows.length < target) {
      const next = [...rows];
      while (next.length < target) {
        const seed = makeBlank();
        const suggested = rosterForTopUp ? pickUnusedName(rosterForTopUp, usedSet) : null;
        next.push(suggested ? setName(seed, suggested) : seed);
      }
      return next;
    }
    // Too many — trim blanks first so user-edited rows survive.
    const named = rows.filter((r) => (r.name ?? '').trim().length > 0);
    const blanks = rows.filter((r) => (r.name ?? '').trim().length === 0);
    return [...named, ...blanks].slice(0, target);
  };
  return [
    ...adjust(
      techs,
      Math.max(0, counts.tech),
      blankTech,
      rosters.technicianRoster,
      usedTechs,
      (row, name) => ({ ...row, name }),
    ),
    ...adjust(
      advisors,
      Math.max(0, counts.advisor),
      () => blankNonTechRow('SERVICE_WRITER'),
      rosters.advisorRoster,
      usedAdvisors,
      (row, name) => ({ ...row, name }),
    ),
    ...adjust(
      managers,
      Math.max(0, counts.manager),
      () => blankNonTechRow('MANAGER'),
      undefined,
      new Set<string>(),
      (row, name) => ({ ...row, name }),
    ),
    ...passThrough,
  ];
}

export function TeamBaysSpecialtyParkingStep({
  initial,
  technicianCount,
  advisorCount = 0,
  managerCount = 0,
  shopHours,
  onBack,
  onFinish,
  isCommitting,
  onDraftChange,
  onCountDelta,
  suggestedTechnicianRoster,
  suggestedAdvisorRoster,
}: TeamBaysSpecialtyParkingStepProps) {
  // Batch 9 seeding rules:
  //   - Start from `initial.technicians` (manual: empty / import:
  //     prefilled rows from buildPage3 / round-trip: the page3 draft
  //     LeanSetupWizard captured from the last visit).
  //   - Reconcile against Step 2 counts so the role group sizes
  //     match the user-confirmed numbers. Top up missing roles with
  //     blanks; trim excess by removing blank-named rows first so
  //     edited rows survive a Back-and-forward.
  //   - We deliberately do NOT auto-seed OWNER / ORG_ADMIN rows. The
  //     beta provisioning flow already grants the signed-in user an
  //     OWNER membership; an extra OWNER row in the wizard would be
  //     redundant and could confuse downstream invite logic (Batch 5).
  //     Pass-through rows (OWNER/ORG_ADMIN) added via Add Team
  //     Member are preserved by the reconciler.
  const seeded: RowWithClientId[] = reconcileRowsAgainstCounts(
    (initial.technicians ?? []) as WizardTeamMember[],
    { tech: technicianCount, advisor: advisorCount, manager: managerCount },
    {
      technicianRoster: suggestedTechnicianRoster,
      advisorRoster: suggestedAdvisorRoster,
    },
  );
  const [technicians, setTechnicians] = useState<RowWithClientId[]>(seeded);
  const [specialties, setSpecialties] = useState<WizardSpecialty[]>(initial.specialties ?? []);
  const [parkingCount, setParkingCount] = useState<number>(initial.parking_spot_count ?? 0);
  const [errors, setErrors] = useState<string[]>([]);

  const updateTech = (clientId: string, patch: Partial<WizardTeamMember>) => {
    setTechnicians((prev) => prev.map((t) => (t._clientId === clientId ? { ...t, ...patch } : t)));
  };

  /**
   * Batch 16-B: change the bay count and keep `bay_types` in lockstep.
   *
   *   - When the count grows, the existing per-bay choices are
   *     preserved and the new slots are appended as Flat (the canonical
   *     `DEFAULT_SETUP_BAY_TYPE`).
   *   - When the count shrinks, the trailing slots are trimmed so the
   *     operator's first-N choices survive. The specialty toggles below
   *     never reference a trimmed slot — `handleDeleteRow` already
   *     re-maps `primary_tech_index` on row removal, and within a
   *     single tech the conversion always targets the LEAD slots, so a
   *     shrink never strands a specialty mid-list.
   *   - When the count is 0 or negative, `bay_types` becomes `[]`
   *     (matching the wizard contract that 0-bay rows produce no
   *     shop_bays records).
   */
  const updateTechBayCount = (clientId: string, nextCount: number) => {
    setTechnicians((prev) =>
      prev.map((t) => {
        if (t._clientId !== clientId) return t;
        const next: BayTypeEnum[] = reconcileBayTypesForCount(
          (t.bay_types ?? []) as ReadonlyArray<BayTypeEnum>,
          nextCount,
        );
        return { ...t, bays_allocated: nextCount, bay_types: next };
      }),
    );
  };

  /**
   * Batch 16-B: replace a single per-bay-slot type without changing
   * the bay count or any other field.
   */
  const updateTechBayTypeAt = (
    clientId: string,
    slotIdx: number,
    nextType: BayTypeEnum,
  ) => {
    setTechnicians((prev) =>
      prev.map((t) => {
        if (t._clientId !== clientId) return t;
        const allocated = typeof t.bays_allocated === 'number' ? t.bays_allocated : 0;
        if (slotIdx < 0 || slotIdx >= allocated) return t;
        const current = reconcileBayTypesForCount(
          (t.bay_types ?? []) as ReadonlyArray<BayTypeEnum>,
          allocated,
        );
        current[slotIdx] = nextType;
        return { ...t, bay_types: current };
      }),
    );
  };

  // Batch 9: emit the current Step 3 draft on every change so the
  // parent (LeanSetupWizard) can preserve it across Back/Forward.
  // Batch 9F: strip the UI-only `_clientId` before crossing the
  // component boundary so the parent's page3 type stays clean.
  useEffect(() => {
    if (!onDraftChange) return;
    onDraftChange({
      technicians: technicians.map(stripClientId),
      specialties,
      parking_spot_count: Number(parkingCount) || 0,
    });
  }, [technicians, specialties, parkingCount, onDraftChange]);

  /**
   * Batch 9: append a blank TECHNICIAN row. The role dropdown lets
   * the user switch to SERVICE_WRITER / MANAGER / OWNER / ORG_ADMIN
   * via the existing handleRoleChange flow. Bumps Step 2's tech count
   * so reconciliation on round-trip doesn't trim it back off.
   *
   * Batch 9C: if the import wizard supplied a suggested technician
   * roster, pull the next name that isn't already in use rather than
   * leaving the row blank. Operator can edit / clear freely.
   */
  const handleAddRow = () => {
    setTechnicians((prev) => {
      const seed = blankTech();
      const usedTechs = new Set(
        prev
          .filter((r) => (r.team_role ?? 'TECHNICIAN') === 'TECHNICIAN')
          .map((r) => (r.name ?? '').trim().toLowerCase())
          .filter((n) => n.length > 0),
      );
      const suggested = suggestedTechnicianRoster
        ? pickUnusedName(suggestedTechnicianRoster, usedTechs)
        : null;
      return [...prev, suggested ? { ...seed, name: suggested } : seed];
    });
    onCountDelta?.({ tech: 1 });
  };

  /**
   * Batch 9F: remove the row whose `_clientId` matches. Specialty
   * `primary_tech_index` values are re-mapped so the wizard never
   * references a missing tech. Step 2's count for the deleted role
   * is decremented to keep Step 2 and Step 3 in sync on round-trip.
   *
   * Pre-9F this took an array index. Newly-added rows interleaved
   * with `onDraftChange` parent re-renders left the click handler
   * closure pointing at a stale index, producing the smoke-test
   * symptom where Add Team Member's row appeared but its Remove
   * button did nothing. Looking up by stable client ID and
   * computing the corresponding index inside `setTechnicians` /
   * `setSpecialties` eliminates the race.
   */
  const handleDeleteRow = (clientId: string) => {
    const idx = technicians.findIndex((r) => r._clientId === clientId);
    if (idx < 0) return;
    const role: TeamRole = (technicians[idx].team_role ?? 'TECHNICIAN') as TeamRole;
    setTechnicians((prev) => prev.filter((r) => r._clientId !== clientId));
    setSpecialties((specs) =>
      specs.flatMap((s) => {
        if (s.primary_tech_index === idx) return [];
        if (s.primary_tech_index > idx) return [{ ...s, primary_tech_index: s.primary_tech_index - 1 }];
        return [s];
      }),
    );
    if (role === 'TECHNICIAN') onCountDelta?.({ tech: -1 });
    else if (role === 'SERVICE_WRITER') onCountDelta?.({ advisor: -1 });
    else if (role === 'MANAGER') onCountDelta?.({ manager: -1 });
  };

  // When the user flips a row's team_role between TECHNICIAN and
  // a non-technician role, we need to fill or drop the tech-only
  // operational fields so submit validation passes and the UI matches.
  // Going TECH → non-tech: drop the optional tech fields so they
  //   don't round-trip stale data; the schema permits them to be
  //   absent for non-TECHNICIAN rows.
  // Going non-tech → TECH: re-seed defaults so the schema's required-
  //   when-TECHNICIAN refinement passes and the operational columns
  //   render with sensible starting values.
  const handleRoleChange = (clientId: string, nextTeamRole: TeamRole) => {
    setTechnicians((prev) =>
      prev.map((row) => {
        if (row._clientId !== clientId) return row;
        if (nextTeamRole === 'TECHNICIAN' && row.team_role !== 'TECHNICIAN') {
          // Batch 16-B: `blankTech()` already seeds `bay_types: ['flat']`
          // because the default bays count is 1. No extra wiring needed.
          const seed = blankTech();
          return { ...seed, _clientId: clientId, name: row.name, team_role: 'TECHNICIAN' };
        }
        if (nextTeamRole !== 'TECHNICIAN' && row.team_role === 'TECHNICIAN') {
          // Batch 16-B: dropping back to a non-tech role drops every
          // technician-only operational field, including `bay_types`,
          // by constructing a fresh row that only carries the team_role
          // + the operator's name. The schema makes `bay_types`
          // optional, so the absence is valid.
          return { _clientId: clientId, name: row.name, team_role: nextTeamRole } as RowWithClientId;
        }
        return { ...row, team_role: nextTeamRole };
      }),
    );
  };

  const isSpecialtySelected = (kind: SpecialtyKind) => specialties.some((s) => s.kind === kind);
  const primaryForSpecialty = (kind: SpecialtyKind) =>
    specialties.find((s) => s.kind === kind)?.primary_tech_index ?? -1;

  // Specialties can only attach to TECHNICIAN rows with ≥1 bay. Use
  // this same predicate for both the auto-pick on toggle and the
  // primary-tech dropdown's option list below.
  const isTechnicianWithBay = (row: WizardTeamMember): boolean =>
    (row.team_role ?? 'TECHNICIAN') === 'TECHNICIAN' && (row.bays_allocated ?? 0) >= 1;

  const toggleSpecialty = (kind: SpecialtyKind) => {
    if (isSpecialtySelected(kind)) {
      setSpecialties((prev) => prev.filter((s) => s.kind !== kind));
    } else {
      const firstEligible = technicians.findIndex(isTechnicianWithBay);
      setSpecialties((prev) => [
        ...prev,
        { kind, primary_tech_index: firstEligible >= 0 ? firstEligible : 0 },
      ]);
    }
  };

  const setPrimary = (kind: SpecialtyKind, techIdx: number) => {
    setSpecialties((prev) =>
      prev.map((s) => (s.kind === kind ? { ...s, primary_tech_index: techIdx } : s)),
    );
  };

  const totalBays = technicians.reduce(
    (sum, t) => sum + (typeof t.bays_allocated === 'number' && Number.isFinite(t.bays_allocated) ? t.bays_allocated : 0),
    0,
  );

  const handleFinish = () => {
    const parsed = page3Schema.safeParse({
      technicians,
      specialties,
      parking_spot_count: Number(parkingCount),
    });
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    void onFinish(parsed.data);
  };

  const warnings = softWarningsForPage3({
    technicians,
    specialties,
    parking_spot_count: parkingCount,
  });

  // Advisory-only: tech hours that drift outside the shop bands. Always
  // computed; never blocks save. The wizard tech rows carry shift_start
  // and shift_end only (no per-day map), so the helper uses simple-mode
  // comparison against the shop's earliest open / latest close.
  const techHoursAdvisories = technicians.flatMap((tech, idx) => {
    if (!shopHours) return [];
    if ((tech.team_role ?? 'TECHNICIAN') !== 'TECHNICIAN') return [];
    const fallbackName = tech.name?.trim() || `Tech ${idx + 1}`;
    return validateTechnicianHoursAgainstShopHours(
      { shift_start: tech.shift_start, shift_end: tech.shift_end },
      shopHours as ShopHours,
      fallbackName,
    ).messages;
  });

  // ─── Step 3 layout (Batch 9D card redesign) ─────────────────────
  //
  // Pre-Batch-9D: 24-col grid + `md:min-w-[1500px]` per row +
  // `overflow-x-auto` wrapper. Worked but felt like a spreadsheet;
  // the absolute-positioned Remove X stuck to the off-screen right
  // edge of the 1500px row, and non-tech rows showed an awkward
  // 14-col vertical filler "Bay / shift details aren't needed".
  //
  // Batch 9D: each team member is its own bordered card with:
  //   - a header band (member label + Remove button — always
  //     visible, never off-screen);
  //   - an identity block (Name + Role);
  //   - an invite block (Email + Invite checkbox);
  //   - a TECHNICIAN-only collapsible sub-block with the
  //     technician operational fields, using responsive
  //     grid-cols-{2,4} for wrap. Non-tech rows render NO filler
  //     text — the absence of the sub-block IS the message.
  //
  // Card width drops back to max-w-4xl (no horizontal scroll
  // needed). Mobile naturally stacks via grid-cols-1.

  return (
    <Card className="w-full max-w-4xl" data-testid="wizard-step-team-bays-parking">
      <CardHeader>
        <CardTitle>Team, Bays, and Parking</CardTitle>
        <CardDescription>
          One row per team member. Bays are generated from each technician's allocation; specialty bays convert an existing bay (no extra physical bay added). You can refine layout in the Birdseye editor anytime.
        </CardDescription>
        <p
          className="text-xs text-muted-foreground mt-2"
          data-testid="wizard-tech-section-helper"
        >
          Add your initial team. Pick a Role for each row — technician
          rows also need a skill and shift. Check
          Invite to email a join link when setup finishes (Org Admin
          invites are added separately through Team Management).
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Batch 9F: row stack uses space-y-5 (was space-y-3) so
            adjacent cards no longer visually blend. The header band
            gets a muted background so each member card has a clear
            top tint; combined with shadow-sm + border-2 the cards
            read as distinct elements. */}
        <div className="space-y-5" data-testid="wizard-tech-rows">
          {technicians.map((tech, idx) => {
            const teamRole: TeamRole = (tech.team_role ?? 'TECHNICIAN') as TeamRole;
            const isTech = teamRole === 'TECHNICIAN';
            const inviteAllowed = isInvitableTeamRole(teamRole);
            const inviteChecked = !!tech.invite && inviteAllowed;
            const memberLabel = tech.name?.trim()
              ? tech.name.trim()
              : `Team member ${idx + 1}`;
            return (
              <div
                key={tech._clientId}
                className="rounded-lg border-2 bg-card shadow-sm"
                data-testid={`wizard-team-row-${idx}`}
                data-client-id={tech._clientId}
              >
                {/* Header band: number badge + member label + Remove
                    button, always visible inline (no absolute
                    positioning, no overflow-x scroll). */}
                <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 rounded-t-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </span>
                    <div
                      className="text-sm font-medium truncate"
                      data-testid={`wizard-team-row-label-${idx}`}
                    >
                      {memberLabel}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDeleteRow(tech._clientId)}
                    disabled={technicians.length <= 1}
                    data-testid={`wizard-team-row-remove-${idx}`}
                    aria-label="Remove team member"
                    title={
                      technicians.length <= 1
                        ? 'At least one team member is required.'
                        : 'Remove this team member'
                    }
                  >
                    <X className="h-3 w-3 mr-1" />
                    Remove
                  </Button>
                </div>

                <div className="space-y-3 p-3">
                  {/* Identity row — Name + Role. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`wizard-tech-name-${idx}`} className="text-xs">Name</Label>
                      <Input
                        id={`wizard-tech-name-${idx}`}
                        placeholder="Team member name"
                        value={tech.name}
                        onChange={(e) => updateTech(tech._clientId,{ name: e.target.value })}
                        data-testid={`wizard-tech-name-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`wizard-team-role-${idx}`} className="text-xs">Role</Label>
                      <select
                        id={`wizard-team-role-${idx}`}
                        className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                        value={teamRole}
                        onChange={(e) => handleRoleChange(tech._clientId, e.target.value as TeamRole)}
                        data-testid={`wizard-team-role-${idx}`}
                      >
                        {TEAM_ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>{TEAM_ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Invite row — Email (flex-grow) + Invite checkbox. */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                    <div className="space-y-1">
                      <Label htmlFor={`wizard-team-email-${idx}`} className="text-xs">Email</Label>
                      <Input
                        id={`wizard-team-email-${idx}`}
                        type="email"
                        placeholder="Optional unless inviting"
                        value={tech.email ?? ''}
                        onChange={(e) => updateTech(tech._clientId,{ email: e.target.value })}
                        data-testid={`wizard-team-email-${idx}`}
                      />
                    </div>
                    <label
                      className="flex items-center gap-2 text-sm pb-2 md:pb-3"
                      htmlFor={`wizard-team-invite-${idx}`}
                    >
                      <input
                        id={`wizard-team-invite-${idx}`}
                        type="checkbox"
                        className="h-4 w-4 rounded border-input"
                        checked={inviteChecked}
                        disabled={!inviteAllowed}
                        onChange={(e) => updateTech(tech._clientId,{ invite: e.target.checked })}
                        data-testid={`wizard-team-invite-${idx}`}
                        title={
                          inviteAllowed
                            ? 'Send a team invitation when setup finishes'
                            : 'Org Admin invites aren’t supported in setup yet — add via Team Management.'
                        }
                      />
                      <span className={inviteAllowed ? '' : 'text-muted-foreground'}>
                        Invite
                      </span>
                    </label>
                  </div>

                  {/* Technician-only operational fields — clean grouped
                      sub-card. Non-tech rows render NOTHING here (no
                      awkward "not needed" filler). */}
                  {isTech && (
                    <div
                      className="rounded border bg-muted/30 p-3 space-y-3"
                      data-testid={`wizard-tech-setup-${idx}`}
                    >
                      <div className="text-xs font-medium uppercase text-muted-foreground">
                        Technician setup
                      </div>
                      {/* No legacy classification picker: the Apprentice /
                          Technician / Foreman `technicians.role` is not a
                          manager choice. The Role above (the team/work
                          role) is the one role system. See
                          LEGACY_TECHNICIAN_ROLE_DOCTRINE. */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">
                            Skill <span className="text-muted-foreground">{tech.skill_tier ?? 5}/10</span>
                          </Label>
                          <Slider
                            min={1}
                            max={10}
                            step={1}
                            value={[tech.skill_tier ?? 5]}
                            onValueChange={(v) => updateTech(tech._clientId,{ skill_tier: v[0] })}
                          />
                        </div>
                      </div>
                      <TechnicianBackgroundFields
                        idPrefix={`wizard-tech-bg-${idx}`}
                        value={{
                          experienceLevel: tech.experience_level ?? null,
                          yearsProfessional: tech.years_professional ?? null,
                        }}
                        onChange={(v) => updateTech(tech._clientId, {
                          experience_level: v.experienceLevel,
                          years_professional: v.yearsProfessional,
                        })}
                      />
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor={`wizard-tech-efficiency-${idx}`} className="text-xs">
                            Efficiency %
                          </Label>
                          <Input
                            id={`wizard-tech-efficiency-${idx}`}
                            type="number"
                            min={50}
                            max={200}
                            value={tech.efficiency_percentage ?? 100}
                            // CORE-G: a person editing this field IS the human
                            // decision, so the provenance is recorded at the
                            // moment it happens. A roster row left at the
                            // seeded 100 carries no source and is created
                            // UNRECORDED — nobody established it.
                            onChange={(e) => updateTech(tech._clientId, {
                              efficiency_percentage: Number(e.target.value),
                              efficiency_source: 'manual',
                            })}
                            data-testid={`wizard-tech-efficiency-${idx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`wizard-tech-buffer-${idx}`} className="text-xs">
                            Buffer %
                          </Label>
                          <Input
                            id={`wizard-tech-buffer-${idx}`}
                            type="number"
                            min={0}
                            max={100}
                            value={tech.buffer_percentage ?? ''}
                            placeholder={NO_BUFFER_SET_LABEL}
                            onChange={(e) => updateTech(tech._clientId, { buffer_percentage: e.target.value === '' ? null : Number(e.target.value) })}
                            data-testid={`wizard-tech-buffer-${idx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`wizard-tech-shift-start-${idx}`} className="text-xs">
                            Shift Start
                          </Label>
                          <Input
                            id={`wizard-tech-shift-start-${idx}`}
                            type="time"
                            value={tech.shift_start ?? '08:00'}
                            onChange={(e) => updateTech(tech._clientId,{ shift_start: e.target.value })}
                            data-testid={`wizard-tech-shift-start-${idx}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor={`wizard-tech-shift-end-${idx}`} className="text-xs">
                            Shift End
                          </Label>
                          <Input
                            id={`wizard-tech-shift-end-${idx}`}
                            type="time"
                            value={tech.shift_end ?? '17:00'}
                            onChange={(e) => updateTech(tech._clientId,{ shift_end: e.target.value })}
                            data-testid={`wizard-tech-shift-end-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor={`wizard-tech-bays-${idx}`} className="text-xs">
                            Bays
                          </Label>
                          <Input
                            id={`wizard-tech-bays-${idx}`}
                            type="number"
                            min={0}
                            value={tech.bays_allocated ?? 1}
                            onChange={(e) =>
                              updateTechBayCount(
                                tech._clientId,
                                Math.max(0, Number(e.target.value) || 0),
                              )
                            }
                            data-testid={`wizard-tech-bays-${idx}`}
                          />
                        </div>
                      </div>

                      {/* Batch 16-B: per-bay type picker. Renders one row
                          per allocated bay using the canonical
                          BAY_TYPE_PICKER_OPTIONS so the picker labels and
                          stored enum values stay locked to the rest of
                          the app. Specialty conversions (selected via
                          the toggles further down the page) lock the
                          corresponding lead slots to the specialty value
                          and disable the dropdown — the existing
                          "specialty consumes a bay" product rule is
                          preserved exactly. */}
                      {(tech.bays_allocated ?? 0) > 0 && (
                        <div
                          className="space-y-2"
                          data-testid={`wizard-tech-bay-setup-${idx}`}
                        >
                          <div className="text-xs font-medium uppercase text-muted-foreground">
                            Bay setup
                          </div>
                          {(() => {
                            const allocated = tech.bays_allocated ?? 0;
                            const slots = reconcileBayTypesForCount(
                              (tech.bay_types ?? []) as ReadonlyArray<BayTypeEnum>,
                              allocated,
                            );
                            // Number of lead bays the specialty toggles
                            // will convert for this tech — see the
                            // identical "first N slots" rule in
                            // generateBayLayout.ts.
                            const specsForTech = specialties.filter(
                              (s) => s.primary_tech_index === idx,
                            );
                            return slots.map((slotType, slotIdx) => {
                              const specForSlot = specsForTech[slotIdx];
                              const effectiveValue: BayTypeEnum = specForSlot
                                ? (specForSlot.kind as BayTypeEnum)
                                : slotType;
                              const lockedBySpecialty = !!specForSlot;
                              return (
                                <div
                                  key={slotIdx}
                                  className="grid grid-cols-1 sm:grid-cols-[7rem_1fr] gap-2 items-center"
                                >
                                  <Label
                                    htmlFor={`wizard-tech-bay-type-${idx}-${slotIdx}`}
                                    className="text-xs"
                                  >
                                    Bay {slotIdx + 1} type
                                  </Label>
                                  <select
                                    id={`wizard-tech-bay-type-${idx}-${slotIdx}`}
                                    className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60"
                                    value={effectiveValue}
                                    disabled={lockedBySpecialty}
                                    title={
                                      lockedBySpecialty
                                        ? `Set by the ${bayTypeToLabel(specForSlot.kind)} specialty selection below — uncheck it to change.`
                                        : undefined
                                    }
                                    onChange={(e) =>
                                      updateTechBayTypeAt(
                                        tech._clientId,
                                        slotIdx,
                                        e.target.value as BayTypeEnum,
                                      )
                                    }
                                    data-testid={`wizard-tech-bay-type-${idx}-${slotIdx}`}
                                  >
                                    {BAY_TYPE_PICKER_OPTIONS.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              );
                            });
                          })()}
                          <p
                            className="text-xs text-muted-foreground"
                            data-testid={`wizard-tech-bay-helper-${idx}`}
                          >
                            You can upload lift/equipment photos later in OptiAssets to improve bay accuracy and asset records.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* Batch 9: Add Team Member appends a TECHNICIAN row (the
              row picks up the next unused name from the suggested
              roster when one is configured — see Batch 9C). Bumps
              Step 2's tech count via onCountDelta so Step 2 ↔ Step 3
              stay in sync. */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddRow}
              data-testid="wizard-add-team-member"
            >
              <Plus className="mr-1 h-3 w-3" />
              Add Team Member
            </Button>
            <div className="text-sm text-muted-foreground" data-testid="wizard-total-bays">
              Total physical bays: <strong>{totalBays}</strong>
            </div>
          </div>
        </div>

        {/* Specialty capabilities */}
        <div className="space-y-2">
          <Label>Specialty bays (optional)</Label>
          <p className="text-xs text-muted-foreground">
            Selecting a specialty converts one of the primary technician's existing bays. It does not add a new bay.
          </p>
          <div className="space-y-2" data-testid="wizard-specialties">
            {SPECIALTY_KINDS.map((kind) => {
              const selected = isSpecialtySelected(kind);
              const primary = primaryForSpecialty(kind);
              return (
                <div key={kind} className="flex items-center gap-3">
                  <label className="flex w-40 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSpecialty(kind)}
                      data-testid={`wizard-specialty-toggle-${kind}`}
                    />
                    {SPECIALTY_LABELS[kind]}
                  </label>
                  {selected && (
                    <select
                      className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                      value={primary}
                      onChange={(e) => setPrimary(kind, Number(e.target.value))}
                      data-testid={`wizard-specialty-primary-${kind}`}
                    >
                      {technicians.map((t, i) => {
                        const eligible = isTechnicianWithBay(t);
                        return (
                          <option key={i} value={i} disabled={!eligible}>
                            {t.name || `Tech ${i + 1}`} {!eligible ? '(no bay)' : ''}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Parking */}
        <div className="space-y-2 max-w-xs">
          <Label htmlFor="wizard-parking">Estimated number of parking spots</Label>
          <Input
            id="wizard-parking"
            type="number"
            min={0}
            value={parkingCount}
            onChange={(e) => setParkingCount(Number(e.target.value))}
            data-testid="wizard-parking-count"
          />
          <p className="text-xs text-muted-foreground">
            We'll generate a starter lot you can drag and refine later in the Birdseye / parking editor.
          </p>
        </div>

        {/* Soft warnings (non-blocking) */}
        {warnings.length > 0 && (
          <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-sm" data-testid="wizard-soft-warnings">
            <strong className="text-amber-700">Heads up:</strong>
            <ul className="list-disc pl-5 mt-1">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {/* Tech-hours advisories — separate block so the source of each
            "heads up" line is unambiguous when both fire. Saving is not
            blocked; this is informational only. */}
        {techHoursAdvisories.length > 0 && (
          <div
            className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-sm"
            data-testid="wizard-tech-hours-advisory"
          >
            <strong className="text-amber-700">Technician hours outside shop hours:</strong>
            <p className="text-xs text-muted-foreground mt-1">
              This is allowed, but scheduling may create before-open or after-close availability.
            </p>
            <ul className="list-disc pl-5 mt-1">
              {techHoursAdvisories.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Hard errors */}
        {errors.length > 0 && (
          <ul className="text-sm text-destructive list-disc pl-5" data-testid="wizard-step-errors">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onBack} disabled={isCommitting}>Back</Button>
          <Button onClick={handleFinish} disabled={isCommitting} data-testid="wizard-finish">
            {isCommitting ? 'Finishing setup…' : 'Finish setup'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
